/**
 * The move list, in the notation from Phase 1.
 *
 * Neither previous build could write a game down at all — moves existed only as pairs of
 * indices into a particular board ordering — so there was nothing to show here and no way
 * to quote a position in a bug report. Clicking an entry rewinds the board to just after
 * that move.
 */

import { type ReactNode, useEffect, useRef } from 'react';

import { Empty } from './primitives.tsx';

/**
 * Just the notation, not a whole match.
 *
 * A local game against the bot has a move log and no match record — nothing on a server,
 * no players, no clock — and it should still be able to show its own scoresheet.
 */
export function MoveList({
  moves,
  ply,
  onSeek,
}: {
  moves: readonly { readonly san: string }[];
  ply: number;
  onSeek(ply: number): void;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const live = ply >= moves.length;

  useEffect(() => {
    if (live) endRef.current?.scrollIntoView({ block: 'end' });
  }, [live, moves.length]);

  if (moves.length === 0) {
    return <Empty>No moves yet.</Empty>;
  }

  // Two half-moves to a row, numbered the way a scoresheet is.
  const rows: Array<[number, string | null, string | null]> = [];
  for (let i = 0; i < moves.length; i += 2) {
    rows.push([
      i / 2 + 1,
      moves[i]?.san ?? null,
      moves[i + 1]?.san ?? null,
    ]);
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='min-h-0 flex-1 overflow-y-auto px-1 py-1'>
        <ol className='font-mono text-[0.8rem]'>
          {rows.map(([number, white, black]) => (
            <li key={number} className='flex items-center gap-1 px-1 py-px'>
              <span className='tabular w-6 shrink-0 text-right text-muted'>
                {number}.
              </span>
              <SanButton
                san={white}
                atPly={(number - 1) * 2 + 1}
                ply={ply}
                onSeek={onSeek}
              />
              <SanButton
                san={black}
                atPly={(number - 1) * 2 + 2}
                ply={ply}
                onSeek={onSeek}
              />
            </li>
          ))}
        </ol>
        <div ref={endRef} />
      </div>
      {
        /* An invisible shortcut is not a feature. Hidden on a phone, which has no keys
          to press and no room to spare for saying so. */
      }
      <p className='hidden shrink-0 items-center gap-2 border-t border-rule px-2 py-1
        font-mono text-[0.62rem] tracking-wide text-muted lg:flex'>
        <Key>&larr;</Key>
        <Key>&rarr;</Key>
        <span>step</span>
        <Key>&uarr;</Key>
        <Key>&darr;</Key>
        <span>start &middot; live</span>
      </p>
    </div>
  );
}

function Key({ children }: { children: ReactNode }) {
  return (
    <kbd className='rounded border border-rule bg-raised px-1 leading-4 text-ink-2'>
      {children}
    </kbd>
  );
}

function SanButton({
  san,
  atPly,
  ply,
  onSeek,
}: {
  san: string | null;
  atPly: number;
  ply: number;
  onSeek(ply: number): void;
}) {
  if (!san) return <span className='flex-1' />;

  const current = ply === atPly;
  return (
    <button
      type='button'
      onClick={() => onSeek(atPly)}
      className={`flex-1 rounded px-1.5 py-0.5 text-left transition-colors
        ${current ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-raised'}`}
    >
      {san}
    </button>
  );
}
